import { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Timestamp } from 'firebase/firestore';
import { useAtomValue } from 'jotai';
import { Block, BlockWithRevisions } from '@/types';
import { firebaseUserAtom } from '@/stores/auth';
import { queryKeys } from '@/lib/queryKeys';
import * as blocksRepository from '@/repositories/blocks.repository';
import * as blocksService from '@/services/blocks.service';
import { logBlockRevision } from './useRevisionLog';

// ============================================
// Types (re-exported for backward compatibility)
// ============================================

export interface UseBlocksOptions {
  date: string; // YYYY-MM-DD format
  includeRevisions?: boolean;
}

export type { CreateBlockInput } from '@/repositories/blocks.repository';

export interface UseBlocksResult {
  blocks: BlockWithRevisions[];
  isLoading: boolean;
  error: Error | null;
  completedCount: number;
  missedCount: number;
  missedMinutes: number;
  totalCount: number;
  createBlock: (input: blocksRepository.CreateBlockInput) => Promise<Block | null>;
  updateBlock: (blockId: string, updates: Partial<Block>) => Promise<boolean>;
  deleteBlock: (blockId: string) => Promise<boolean>;
}

// ============================================
// Hook
// ============================================

export function useBlocks({ date, includeRevisions = true }: UseBlocksOptions): UseBlocksResult {
  const firebaseUser = useAtomValue(firebaseUserAtom);
  const queryClient = useQueryClient();

  const [isListenerInitialized, setIsListenerInitialized] = useState(false);
  const prevKeyRef = useRef<string | null>(null);

  const userId = firebaseUser?.uid;
  const queryKey = queryKeys.blocks.byDate(userId, date);
  const cacheKey = `${userId}-${date}`;

  // React Query for caching and state management
  const { data: blocks = [], error } = useQuery<BlockWithRevisions[], Error>({
    queryKey,
    queryFn: async () => [],
    enabled: !!userId,
    staleTime: Infinity,
  });

  // Real-time Firestore listener
  useEffect(() => {
    if (prevKeyRef.current !== cacheKey) {
      prevKeyRef.current = cacheKey;
      setIsListenerInitialized(false);
    }

    if (!userId) {
      setIsListenerInitialized(true);
      return;
    }

    let isCleanedUp = false;

    const timeoutId = setTimeout(() => {
      if (!isCleanedUp && !isListenerInitialized) {
        console.warn('Firestore listener timeout - marking as initialized');
        setIsListenerInitialized(true);
      }
    }, 2000);

    const unsubscribe = blocksRepository.subscribeToBlocks(
      userId,
      date,
      (blocksData) => {
        if (isCleanedUp) return;
        const sorted = blocksService.sortBlocksByStartTime(blocksData);
        queryClient.setQueryData(queryKey, sorted);
        setIsListenerInitialized(true);
      },
      (_error) => {
        if (!isCleanedUp) {
          setIsListenerInitialized(true);
        }
      }
    );

    return () => {
      isCleanedUp = true;
      clearTimeout(timeoutId);
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  // Compute progress stats using service
  const { completedCount, missedCount, totalCount, missedMinutes } = useMemo(
    () => blocksService.getBlockProgress(blocks),
    [blocks]
  );

  // Create a new block
  const createBlock = useCallback(
    async (input: blocksRepository.CreateBlockInput): Promise<Block | null> => {
      if (!userId) {
        console.error('User not authenticated');
        return null;
      }

      try {
        const newBlock = await blocksRepository.createBlock(userId, input);

        // Optimistically update cache
        const currentBlocks = queryClient.getQueryData<BlockWithRevisions[]>(queryKey) || [];
        const newBlockWithRevisions: BlockWithRevisions = { ...newBlock, revisions: [] };
        const updatedBlocks = blocksService.sortBlocksByStartTime([
          ...currentBlocks,
          newBlockWithRevisions,
        ]);
        queryClient.setQueryData(queryKey, updatedBlocks);

        // Log revision (fire and forget)
        logBlockRevision(userId, input.date, {
          type: 'add',
          blockId: newBlock.id,
          blockTitle: input.title.trim() || 'Untitled Block',
          addedStartTime: input.startTime,
          addedDuration: input.duration,
        }).catch((err) => console.error('Failed to log block creation:', err));

        return newBlock;
      } catch (error) {
        console.error('Error creating block:', error);
        return null;
      }
    },
    [userId, queryClient, queryKey]
  );

  // Update an existing block
  const updateBlock = useCallback(
    async (blockId: string, updates: Partial<Block>): Promise<boolean> => {
      if (!userId) {
        console.error('User not authenticated');
        return false;
      }

      const currentBlock = blocks.find((b) => b.id === blockId);

      // Track duration history if duration is being changed
      const durationHistoryUpdates: Partial<Block> = {};
      if (
        currentBlock &&
        updates.duration !== undefined &&
        updates.duration !== currentBlock.duration
      ) {
        if (currentBlock.originalDuration === undefined) {
          durationHistoryUpdates.originalDuration = currentBlock.duration;
        }
        durationHistoryUpdates.durationChangedAt = Timestamp.now();
      }

      const finalUpdates = { ...updates, ...durationHistoryUpdates };

      // Optimistic update
      const currentBlocks = queryClient.getQueryData<BlockWithRevisions[]>(queryKey) || [];
      const blockIndex = currentBlocks.findIndex((b) => b.id === blockId);
      if (blockIndex !== -1) {
        const existingBlock = currentBlocks[blockIndex];
        const updatedBlock = { ...existingBlock, ...finalUpdates };
        const updatedBlocks = [...currentBlocks];
        updatedBlocks[blockIndex] = updatedBlock;
        queryClient.setQueryData(queryKey, blocksService.sortBlocksByStartTime(updatedBlocks));
      }

      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Update timed out after 10 seconds')), 10000);
      });

      try {
        const updateOperation = async () => {
          // Store current state as revision before updating
          if (currentBlock) {
            await blocksRepository.createBlockRevision(
              userId,
              blockId,
              currentBlock,
              Object.keys(finalUpdates)
            );
          }

          await blocksRepository.updateBlock(userId, blockId, finalUpdates);

          // Log revision based on what changed (fire and forget)
          if (currentBlock) {
            const blockTitle = updates.title ?? currentBlock.title ?? 'Untitled Block';
            const startTimeChanged =
              updates.startTime !== undefined && updates.startTime !== currentBlock.startTime;
            const durationChanged =
              updates.duration !== undefined && updates.duration !== currentBlock.duration;
            const titleChanged =
              updates.title !== undefined && updates.title !== currentBlock.title;
            const notesChanged =
              updates.notes !== undefined && updates.notes !== currentBlock.notes;
            const categoryChanged =
              updates.categoryId !== undefined && updates.categoryId !== currentBlock.categoryId;
            const colorChanged =
              updates.color !== undefined && updates.color !== currentBlock.color;

            if (startTimeChanged) {
              logBlockRevision(userId, currentBlock.date, {
                type: 'move',
                blockId,
                blockTitle,
                fromStartTime: currentBlock.startTime,
                fromDuration: currentBlock.duration,
                toStartTime: updates.startTime,
                toDuration: updates.duration ?? currentBlock.duration,
              }).catch((err) => console.error('Failed to log block move:', err));
            } else if (durationChanged && !startTimeChanged) {
              logBlockRevision(userId, currentBlock.date, {
                type: 'resize',
                blockId,
                blockTitle,
                oldDuration: currentBlock.duration,
                newDuration: updates.duration,
              }).catch((err) => console.error('Failed to log block resize:', err));
            } else if (
              (titleChanged || notesChanged || categoryChanged || colorChanged) &&
              !startTimeChanged &&
              !durationChanged
            ) {
              logBlockRevision(userId, currentBlock.date, {
                type: 'edit',
                blockId,
                blockTitle,
              }).catch((err) => console.error('Failed to log block edit:', err));
            }
          }

          return true;
        };

        return await Promise.race([updateOperation(), timeoutPromise]);
      } catch (error) {
        console.error('Error updating block:', error);
        queryClient.invalidateQueries({ queryKey });
        return false;
      }
    },
    [userId, blocks, queryClient, queryKey]
  );

  // Delete a block (mark as cancelled)
  const deleteBlock = useCallback(
    async (blockId: string): Promise<boolean> => {
      if (!userId) {
        console.error('User not authenticated');
        return false;
      }

      const blockToDelete = blocks.find((b) => b.id === blockId);

      try {
        await blocksRepository.deleteBlock(userId, blockId);

        // Log deletion (fire and forget)
        if (blockToDelete) {
          logBlockRevision(userId, blockToDelete.date, {
            type: 'delete',
            blockId,
            blockTitle: blockToDelete.title || 'Untitled Block',
            deletedStartTime: blockToDelete.startTime,
            deletedDuration: blockToDelete.duration,
          }).catch((err) => console.error('Failed to log block deletion:', err));
        }

        return true;
      } catch (error) {
        console.error('Error deleting block:', error);
        return false;
      }
    },
    [userId, blocks]
  );

  // Loading state
  const hasBlocks = blocks.length > 0;
  const actualIsLoading = !!userId && !isListenerInitialized && !hasBlocks;

  return {
    blocks,
    isLoading: actualIsLoading,
    error: error ?? null,
    completedCount,
    missedCount,
    missedMinutes,
    totalCount,
    createBlock,
    updateBlock,
    deleteBlock,
  };
}

// ============================================
// Re-exported helpers from service
// ============================================

export const formatDateKey = blocksService.formatDateKey;
export const getWeekDates = blocksService.getWeekDates;
export const minutesToTimeString = blocksService.minutesToTimeString;
export const getBlockEndTime = blocksService.getBlockEndTime;
